import { create } from "zustand";
import { mountsApi, type MountsState, type MountTrainResult, type PlayerMountView } from "../api/mounts";
import { useGameStore } from "./useGameStore";

/** Hero combat stats include stacked mount bonuses — refresh when mounts change on the server. */
export async function refreshHeroIfMountStatsChanged(options: {
  levelsGained?: number;
  ownedCountBefore?: number;
  ownedCountAfter?: number;
  rewardType?: string;
}): Promise<void> {
  const levelsGained = options.levelsGained ?? 0;
  const ownedBefore = options.ownedCountBefore ?? 0;
  const ownedAfter = options.ownedCountAfter ?? ownedBefore;
  const rewardType = options.rewardType ?? "";

  if (levelsGained > 0 || ownedAfter > ownedBefore || rewardType === "mount_card") {
    await useGameStore.getState().refreshHero();
  }
}

function countOwnedMounts(state: MountsState | null): number {
  return state?.mounts.filter((m) => m.owned).length ?? 0;
}

interface MountsStore {
  state: MountsState | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  message: string | null;

  refresh: () => Promise<void>;
  train: (mountCode: string, stones?: number) => Promise<MountTrainResult>;
  clearMessage: () => void;
}

export const useMountsStore = create<MountsStore>((set) => ({
  state: null,
  loading: false,
  actionLoading: false,
  error: null,
  message: null,

  refresh: async () => {
    const ownedBefore = countOwnedMounts(useMountsStore.getState().state);
    set({ loading: true, error: null });
    try {
      const state = await mountsApi.getState();
      set({ state });
      await refreshHeroIfMountStatsChanged({
        ownedCountBefore: ownedBefore,
        ownedCountAfter: countOwnedMounts(state),
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load mounts" });
    } finally {
      set({ loading: false });
    }
  },

  train: async (mountCode, stones = 1) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const result = await mountsApi.train(mountCode, stones);
      set((s) => {
        if (!s.state) return { message: result.message };
        const mounts = s.state.mounts.map((m) =>
          m.mount_code === mountCode ? result.mount : m,
        );
        return {
          message: result.message,
          state: {
            ...s.state,
            mounts,
            totals: result.totals,
            training_stones: result.training_stones,
          },
        };
      });
      await refreshHeroIfMountStatsChanged({ levelsGained: result.levels_gained });
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Training failed";
      set({ error: message });
      throw e;
    } finally {
      set({ actionLoading: false });
    }
  },

  clearMessage: () => set({ message: null, error: null }),
}));

export function findMount(state: MountsState | null, code: string): PlayerMountView | undefined {
  return state?.mounts.find((m) => m.mount_code === code);
}
